import { useMemo, useState } from "react";
import { ExportOutlined, MessageOutlined } from "@ant-design/icons";
import { Button, Select, Space, Table, Tabs, Tag, Typography } from "antd";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { chatActions, teamSelectors } from "@fitness-app/app-store";
import { TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { ChatAgentSuggestionReviewControls } from "~/modules/AI/components/ChatAgentSuggestionReviewControls";
import {
  usePendingChatAgentSuggestions,
  type PendingChatAgentSuggestion,
  type PendingChatAgentSuggestionStatus,
} from "~/modules/AI/hooks/usePendingChatAgentSuggestions";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const ALL_TRAINERS_FILTER_ID = "all";
const DEFAULT_CHAT_AGENT_STATUSES: PendingChatAgentSuggestionStatus[] = ["suggested"];

const renderPreview = (value: string | null | undefined) => {
  if (!value) {
    return <span>-</span>;
  }

  return (
    <Typography.Paragraph className="!mb-0 max-w-[360px]" ellipsis={{ rows: 3, tooltip: value }}>
      {value}
    </Typography.Paragraph>
  );
};

const getFullName = ({
  firstName,
  lastName,
}: {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
}) => `${firstName ?? ""} ${lastName ?? ""}`.trim();

export default function AI() {
  const { t } = useTranslation(["common", "dashboard"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isHeadCoach } = useUserRole();
  const [selectedTrainers, setSelectedTrainers] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] =
    useState<PendingChatAgentSuggestionStatus[]>(DEFAULT_CHAT_AGENT_STATUSES);
  const trainers = useAppSelector(teamSelectors.getTrainersTeam) ?? [];
  const fetchingExtraChannel = useAppSelector((store) => store.chat.channelsListStatus.fetchingExtraChannel);

  const { data, isLoading, isFetching } = usePendingChatAgentSuggestions({
    statuses: selectedStatuses,
    selectedTrainers,
  });

  const activeTrainers = useMemo(
    () =>
      trainers.filter(
        (trainer) =>
          trainer.status === TeamMemberStatus.ACTIVE &&
          (trainer.role === TeamMemberRole.REGULAR_TRAINER || trainer.role === TeamMemberRole.SHARED_TRAINER),
      ),
    [trainers],
  );

  const trainersById = useMemo(() => keyBy(trainers, "id"), [trainers]);
  const statusOptions = useMemo(
    () => [
      {
        label: t("dashboard:ai.chatAgents.status.suggested"),
        value: "suggested" satisfies PendingChatAgentSuggestionStatus,
      },
      {
        label: t("dashboard:ai.chatAgents.status.accepted"),
        value: "accepted" satisfies PendingChatAgentSuggestionStatus,
      },
      {
        label: t("dashboard:ai.chatAgents.status.dismissed"),
        value: "dismissed" satisfies PendingChatAgentSuggestionStatus,
      },
    ],
    [t],
  );

  const trainerOptions = useMemo(
    () => [
      ...activeTrainers.map((trainer) => ({
        label:
          getFullName({
            firstName: trainer.user?.firstName,
            lastName: trainer.user?.lastName,
          }) || t("dashboard:ai.chatAgents.unknownTrainer"),
        value: trainer.id,
      })),
    ],
    [activeTrainers, t],
  );

  const handleTrainerChange = (value: string[]) => {
    if (value.length > 1) {
      setSelectedTrainers(value.filter((item) => item !== ALL_TRAINERS_FILTER_ID));
      return;
    }

    setSelectedTrainers(value);
  };

  const handleStatusChange = (value: PendingChatAgentSuggestionStatus[]) => {
    setSelectedStatuses(value.length > 0 ? value : DEFAULT_CHAT_AGENT_STATUSES);
  };

  const renderStatusTag = (status: PendingChatAgentSuggestionStatus | null | undefined) => {
    if (!status) {
      return <span>-</span>;
    }

    const colorMap: Record<PendingChatAgentSuggestionStatus, string> = {
      suggested: "gold",
      accepted: "green",
      dismissed: "red",
    };

    return <Tag color={colorMap[status]}>{t(`dashboard:ai.chatAgents.status.${status}`)}</Tag>;
  };

  const getTrainerLabel = (trainerId: string | null) => {
    if (!trainerId) {
      return t("dashboard:ai.chatAgents.unknownTrainer");
    }

    const trainer = trainersById[trainerId];

    return (
      getFullName({
        firstName: trainer?.user?.firstName,
        lastName: trainer?.user?.lastName,
      }) || t("dashboard:ai.chatAgents.unknownTrainer")
    );
  };

  const tabs = [
    {
      key: "chat-agents",
      label: `${t("dashboard:ai.tabs.chatAgents")} (${data?.count ?? 0})`,
      children: (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1">
              <Typography.Title level={4} className="!mb-0">
                {t("dashboard:ai.chatAgents.title")}
              </Typography.Title>
              <Typography.Text type="secondary">{t("dashboard:ai.chatAgents.description")}</Typography.Text>
            </div>

            <Select
              mode="multiple"
              value={selectedTrainers}
              onChange={handleTrainerChange}
              options={trainerOptions}
              style={{ minWidth: 320, maxWidth: 500 }}
              placeholder={t("dashboard:ai.chatAgents.filters.placeholder")}
            />
            <Select
              mode="multiple"
              value={selectedStatuses}
              onChange={handleStatusChange}
              options={statusOptions}
              style={{ minWidth: 260 }}
              placeholder={t("dashboard:ai.chatAgents.filters.statusPlaceholder")}
            />
          </div>

          <Table
            rowKey="id"
            dataSource={data?.messages || []}
            size="middle"
            loading={isLoading || isFetching}
            scroll={{ x: true }}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            locale={{
              emptyText: t("dashboard:ai.chatAgents.empty"),
            }}
          >
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.client")}
              dataIndex="trainee"
              key="trainee"
              render={(_, row) => (
                <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.traineeId}`)}>
                  {getFullName({
                    firstName: row.trainee?.firstName,
                    lastName: row.trainee?.lastName,
                  }) || row.trainee?.email}
                </Button>
              )}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.trainer")}
              dataIndex="agentReplyOwnerTeamMemberId"
              key="agentReplyOwnerTeamMemberId"
              render={(trainerId: string | null) => <Tag color="geekblue">{getTrainerLabel(trainerId)}</Tag>}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.status")}
              dataIndex="agentReplyStatus"
              key="agentReplyStatus"
              render={(status: PendingChatAgentSuggestionStatus | null | undefined) => renderStatusTag(status)}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.clientMessage")}
              dataIndex={["message", "content"]}
              key="clientMessage"
              render={(content: string | null | undefined) => renderPreview(content)}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.suggestedReply")}
              dataIndex="agentReplyText"
              key="agentReplyText"
              render={(content: string | null | undefined) => renderPreview(content)}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.generatedAt")}
              dataIndex="agentReplyGeneratedAt"
              key="agentReplyGeneratedAt"
              render={(value: string | null, row) => <span>{new Date(value || row.createdAt).toLocaleString()}</span>}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.review")}
              dataIndex="review"
              key="review"
              width={260}
              render={(_, row) => <ChatAgentSuggestionReviewControls row={row} />}
            />
            <Table.Column<PendingChatAgentSuggestion>
              title={t("dashboard:ai.chatAgents.table.actions")}
              dataIndex="actions"
              key="actions"
              render={(_, row) => (
                <Space size={4}>
                  <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.traineeId}`)}>
                    {t("common:details")}
                  </Button>
                  <Button
                    icon={<ExportOutlined />}
                    title={t<string>("dashboard:ai.chatAgents.openClientInNewTab")}
                    href={`${window.location.origin}/trainee/details/${row.traineeId}`}
                    target="_blank"
                    type="link"
                  />
                  {row.channelId && row.message?.authorId ? (
                    <Button
                      icon={<MessageOutlined />}
                      style={{ paddingLeft: 2, paddingRight: 2 }}
                      title={t<string>("dashboard:ai.chatAgents.openChat")}
                      loading={fetchingExtraChannel}
                      onClick={() =>
                        dispatch(
                          chatActions.findAndAddChannelToList({
                            userId: row.message?.authorId || "",
                            channelId: String(row.channelId),
                            isHeadCoach,
                          }),
                        )
                      }
                      type="link"
                    />
                  ) : null}
                </Space>
              )}
            />
          </Table>
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm">
      <Tabs defaultActiveKey="chat-agents" items={tabs} />
    </div>
  );
}
